import { InputBase, Stack } from '@mui/material';
import { useId } from 'react';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import Typography from '../../ui/Typography/Typography';
import InlineEditDisplay from '../InlineEditDisplay/InlineEditDisplay';
import { useInlineEdit } from '../hooks/useInlineEdit';
import { inlineEditErrorOutline } from '../inlineEdit.utils';
import { InlineEditFieldProps } from './inlineEditField.types';

const InlineEditField = ({
  value,
  onConfirm,
  displayValue,
  isDisabled = false,
  isMuted = false,
  isMultiline = false,
  errorMessage,
  validate,
  placeholder,
  testId,
}: InlineEditFieldProps) => {
  const { translate } = useTranslation();
  const errorMessageId = useId();

  const {
    error,
    isEditing,
    draftValue,
    handleStartEditing,
    handleChangeDraft,
    handleBlur,
    handleKeyDown,
  } = useInlineEdit<string>({
    value,
    onConfirm,
    isDisabled,
    validate,
    shouldConfirmOnEnter: !isMultiline,
  });

  const translatedPlaceholder = placeholder ? translate(placeholder) : undefined;
  const editingError = error ?? errorMessage;

  if (isEditing) {
    return (
      <Stack sx={{ gap: 0.25 }}>
        <InputBase
          value={draftValue}
          autoFocus
          fullWidth
          error={!!editingError}
          multiline={isMultiline}
          placeholder={translatedPlaceholder}
          inputProps={{
            'test-id': testId,
            'aria-describedby': editingError ? errorMessageId : undefined,
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onChange={(event) => handleChangeDraft(event.target.value)}
          sx={(theme) => ({
            ...theme.typography.body2,
            color: 'text.primary',
            backgroundColor: 'action.selected',
            borderRadius: `${theme.shape.borderRadiusMd}px`,
            padding: theme.spacing(0.5, 0.75),
            ...inlineEditErrorOutline(theme, !!editingError),
            '& .MuiInputBase-input': {
              padding: 0,
              height: 'auto',
            },
          })}
        />
        {editingError && (
          <Typography
            id={errorMessageId}
            variant="micro1"
            color="error.main"
            sx={{ paddingX: 0.75 }}
          >
            {editingError}
          </Typography>
        )}
      </Stack>
    );
  }

  const isEmpty = value.length === 0;

  return (
    <InlineEditDisplay
      isEmpty={isEmpty}
      isDisabled={isDisabled}
      isMuted={isMuted}
      isMultiline={isMultiline}
      errorMessage={errorMessage}
      placeholder={placeholder}
      testId={testId}
      onStartEditing={handleStartEditing}
    >
      {displayValue || value}
    </InlineEditDisplay>
  );
};

export default InlineEditField;
